import {ChangeEvent, useCallback, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {getGuestId} from '~/common/fabric/User/reducers/user';
import {parseUSPhoneNumber, valid_email as validateEmail} from '~/common/utils';
import {isFabricWebview} from '~/common/utils/webview';
import {PhoneInput} from '~/ViewInvitation/common/RSVPModalVariantAB/steps/components/PhoneInput';
import {PlusOneInput} from '~/ViewInvitation/common/RSVPModalVariantAB/steps/components/PlusOneInput/PlusOneInput';
import {Header} from '~/ViewInvitation/common/RSVPModalVariantAB/steps/components/Step/components/Header/Header';
import {Step} from '~/ViewInvitation/common/RSVPModalVariantAB/steps/components/Step/Step';
import {Payload, RsvpStatuses} from '~/ViewInvitation/common/RSVPModalVariantAB/types';
import {
  selectAllowGuestsToInviteMore,
  selectMaxAdditionalGuests,
} from '~/ViewInvitation/selectors/eventDetails';
import {
  selectCanShowMarketingOptIn,
  selectGuestEmail,
  selectGuestOf,
  selectHasRsvpd,
  selectIsSMSInvite,
  selectName,
  selectPhone,
  selectSmsOptIn,
  selectTotalAdults,
  selectTotalKids,
} from '~/ViewInvitation/selectors/rsvp';
import {Button} from '~library/atoms/Button';
import {Checkbox} from '~library/atoms/Checkbox';
import {Input} from '~library/atoms/Input';
import {FakeInput} from './components/FakeInput/FakeInput';
import {OptinAndWarning} from './components/OptinAndWarning/OptinAndWarning';

type CollectInfoVariantProps = {
  status: RsvpStatuses;
  onCtaClick: (payload: Payload) => void;
  onClickClose: () => void;
};

export const CollectInfoVariant = ({status, onCtaClick, onClickClose}: CollectInfoVariantProps) => {
  const guestEmail = useSelector(selectGuestEmail);
  const name = useSelector(selectName);
  const phone = useSelector(selectPhone);
  const rsvpdAdults = useSelector(selectTotalAdults);
  const rsvpdKids = useSelector(selectTotalKids);
  const maxAdditionalGuests = useSelector(selectMaxAdditionalGuests);
  const allowGuestsToInviteMore = useSelector(selectAllowGuestsToInviteMore);
  const guestOf = useSelector(selectGuestOf);
  const isSMSInvite = useSelector(selectIsSMSInvite);
  const guestId = useSelector(getGuestId);
  const smsOptin = useSelector(selectSmsOptIn);
  const hasRsvpd = useSelector(selectHasRsvpd);
  const canShowMarketingOptIn = useSelector(selectCanShowMarketingOptIn);
  const allowGuestsToInvite = !!allowGuestsToInviteMore && !!maxAdditionalGuests && !guestOf;
  const [replyAsName, setReplyAsName] = useState(name);
  const [replyAsEmail, setReplyAsEmail] = useState(guestEmail);
  const [replyAsPhone, setReplyAsPhone] = useState(phone);
  const [marketingCheckboxChecked, setMarketingCheckboxChecked] = useState(true);
  const [rsvpComment, setRsvpComment] = useState('');
  const [notYou, setNotYou] = useState(false);
  const [marketingOptinSubmitted, setMarketingOptinSubmitted] = useState(hasRsvpd);

  const [numAdults, setNumAdults] = useState(rsvpdAdults);
  const [numKids, setNumKids] = useState(rsvpdKids);
  const [nameEditMode, setNameEditMode] = useState(!name);
  const [emailIsValid, setEmailIsValid] = useState(
    guestEmail && validateEmail(guestEmail).is_valid
  );
  const [showEmailError, setShowEmailError] = useState(false);
  const [showMarketingOptIn, setShowMarketingOptIn] = useState(false);
  const [smsOptIn, setSmsOptIn] = useState(() => {
    if (isSMSInvite) {
      return true;
    }
    if (hasRsvpd) {
      return smsOptin;
    }
    return false;
  });

  const forceRSVPForm = !guestEmail || !name;
  const isUserAccessingFromCanada = window.request_data?.country === 'CA';

  const phoneIsValid = parseUSPhoneNumber(replyAsPhone).is_valid;
  useEffect(() => {
    const webview = isFabricWebview();
    if (!webview && canShowMarketingOptIn && !marketingOptinSubmitted) {
      const isInExperimentVariant = evite.experiments.inVariation(
        'test_rsvp_marketing_checkbox',
        'variant',
        false
      );
      setShowMarketingOptIn(isInExperimentVariant);
    } else {
      setShowMarketingOptIn(false);
    }
  }, [canShowMarketingOptIn, marketingOptinSubmitted]);
  useEffect(() => {
    setReplyAsName(name);
    setReplyAsPhone(phone);
    setReplyAsEmail(guestEmail);

    if (!name) {
      setNameEditMode(true);
    }

    setNumAdults(rsvpdAdults);
    setNumKids(rsvpdKids);
  }, [name, phone, guestEmail, rsvpdAdults, rsvpdKids]);

  useEffect(() => {
    const {is_valid: isValid} = validateEmail(replyAsEmail);
    setEmailIsValid(isValid);
  }, [replyAsEmail]);

  const onEmailBlurHandler = useCallback(() => {
    const {is_valid: isValid} = validateEmail(replyAsEmail);
    setShowEmailError(replyAsEmail && !isValid);
  }, [replyAsEmail]);

  const onEmailFocusHandler = useCallback(() => setShowEmailError(false), []);

  const onNotYouPress = useCallback(() => {
    setReplyAsName('');
    setReplyAsEmail('');
    setReplyAsPhone('');
    setNotYou(true);
  }, []);

  const onEditNameClick = useCallback(() => {
    setNameEditMode(true);
  }, []);

  const onPhoneChangeHandler = useCallback((value: string) => {
    setSmsOptIn(parseUSPhoneNumber(value).is_valid);
    setReplyAsPhone(value);
  }, []);

  const onCtaClickHandler = useCallback(async () => {
    const email = replyAsEmail?.trim() || guestEmail;
    setMarketingOptinSubmitted(true);
    return onCtaClick({
      // biome-ignore-start lint/style/useNamingConvention: API expects in snake_case
      name: replyAsName.trim(),
      email,
      sms_optin: smsOptIn,
      phone: isUserAccessingFromCanada ? null : replyAsPhone,
      response: status.toLowerCase(),
      adults_in_party: numAdults,
      kids_in_party: numKids,
      comments: rsvpComment,
      marketing_optin_checked: marketingCheckboxChecked,
      showed_marketing_optin: showMarketingOptIn,
      guest_id:
        guestId && (!replyAsEmail || guestEmail === replyAsEmail?.trim() || isSMSInvite)
          ? guestId
          : undefined,
    });
    // biome-ignore-end lint/style/useNamingConvention: API expects in snake_case
  }, [
    guestEmail,
    guestId,
    smsOptIn,
    isSMSInvite,
    isUserAccessingFromCanada,
    numAdults,
    numKids,
    onCtaClick,
    replyAsEmail,
    replyAsName,
    replyAsPhone,
    rsvpComment,
    status,
    showMarketingOptIn,
    marketingCheckboxChecked,
  ]);

  return (
    <Step header={<Header onClose={onClickClose} title="Complete your RSVP" />}>
      {nameEditMode ? (
        <Input
          data-qa-id="replyas-name"
          value={replyAsName}
          label={`RSVPing "${status.toUpperCase()}" as*`}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setReplyAsName(e.target.value)}
          placeholder="First and last name"
        />
      ) : (
        <FakeInput
          dataQaId="replyas-name"
          value={replyAsName}
          label={`RSVPing "${status.toUpperCase()}" as*`}
          onEditClick={onEditNameClick}
        />
      )}

      {(notYou || forceRSVPForm) && (
        <Input
          data-qa-id="replyas-email"
          value={replyAsEmail}
          label="Email*"
          type="email"
          autoComplete="email"
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setReplyAsEmail(e.target.value)}
          placeholder="email@example.com"
          onFocus={onEmailFocusHandler}
          onBlur={onEmailBlurHandler}
          error={showEmailError && 'Oops! Please use a valid email address.'}
        />
      )}

      {!isSMSInvite && !isUserAccessingFromCanada && (
        <PhoneInput value={replyAsPhone} onChange={onPhoneChangeHandler} />
      )}

      {!forceRSVPForm && allowGuestsToInvite && (
        <Button variant="link" size="small" data-qa-id="rsvp-not-you" onClick={onNotYouPress}>
          Not you?
        </Button>
      )}

      <PlusOneInput
        status={status}
        numKids={numKids}
        numAdults={numAdults}
        onNumberOfKidsChange={setNumKids}
        onNumberOfAdultsChange={setNumAdults}
      />

      <Input
        data-qa-id="rsvp-comment"
        value={rsvpComment}
        label="Leave a comment"
        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setRsvpComment(e.target.value)}
        placeholder="Write a message for the host and guests."
        maxLength={300}
      />
      <Button
        variant="primary"
        size="large"
        data-qa-id="rsvp-continue"
        onClick={onCtaClickHandler}
        className="rsvp-step__continue-cta"
        disabled={!emailIsValid || !replyAsName || (replyAsPhone && !phoneIsValid)}
      >
        Continue
      </Button>
      <div className="rsvp-step__footer--variant">
        {showMarketingOptIn && (
          <Checkbox
            id="marketing-opt-in"
            label={
              <span className="marketing-checkbox-text">
                Send me emails with party ideas, offers, and news from Evite.
              </span>
            }
            onChange={() => setMarketingCheckboxChecked(!marketingCheckboxChecked)}
            checked={marketingCheckboxChecked}
            data-qa-id="signup-marketing-checkbox"
            checkboxColor="black"
          />
        )}
        {!isSMSInvite && !isUserAccessingFromCanada && (
          <OptinAndWarning
            setSmsOptIn={setSmsOptIn}
            smsOptIn={smsOptIn}
            phone={replyAsPhone}
            preText="By clicking “Continue” & entering your phone number,"
          />
        )}

        {isSMSInvite && !isUserAccessingFromCanada && (
          <OptinAndWarning
            setSmsOptIn={setSmsOptIn}
            smsOptIn={smsOptIn}
            phone={replyAsPhone}
            preText="By checking this box,"
          />
        )}
      </div>
    </Step>
  );
};
